import { useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useT } from "../../i18n";
import { appById, APP_IDS, PRIVACY_URL, TERMS_URL } from "../../constants/mintlabApps";
import SiteShell from "./SiteShell";

const CATEGORIES = [
  { value: "crash", labelKey: "form.cat.crash" },
  { value: "purchase", labelKey: "form.cat.purchase" },
  { value: "ads", labelKey: "form.cat.ads" },
  { value: "transfer", labelKey: "form.cat.transfer" },
  { value: "feature", labelKey: "form.cat.feature" },
  { value: "other", labelKey: "form.cat.other" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Local dev (npm start) has no backend, so call the production API directly.
// /api/support sets CORS "*", so the cross-origin POST works. On prod this is
// "" → relative, unchanged.
const API_BASE = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
  ? "https://better-everyday.app"
  : "";

export default function SupportPage() {
  const t = useT();
  const [params] = useSearchParams();
  const appId = params.get("app");
  const app = useMemo(() => (appId && APP_IDS.has(appId) ? appById(appId) : null), [appId]);

  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [agree, setAgree] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Anti-spam (vô hình): honeypot + thời điểm mở form
  const [hp, setHp] = useState("");
  const mountedAt = useRef(Date.now());

  function validate() {
    const e = {};
    if (!EMAIL_RE.test(email.trim())) e.email = t("form.err_email");
    if (!category) e.category = t("form.err_category");
    if (!description.trim()) e.description = t("form.err_desc");
    if (!agree) e.agree = t("form.err_agree");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev) {
    ev.preventDefault();
    setFormError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/support`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          category,
          description: description.trim(),
          marketing,
          app_id: appId && APP_IDS.has(appId) ? appId : null,
          locale: document.documentElement.getAttribute("lang") || "en",
          hp,
          elapsed_ms: Date.now() - mountedAt.current,
        }),
      });
      if (!res.ok) throw new Error("request failed");
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (_) {
      setFormError(t("form.err_generic"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SiteShell>
      <div className="ml-form-wrap ml-container">
        <div className="ml-card">
          {done ? (
            <div className="ml-success">
              <div className="ml-success-badge" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12.5 10 17.5 19 7" />
                </svg>
              </div>
              <h2>{t("form.success_title")}</h2>
              <p>{t("form.success_msg")}</p>
              <button
                type="button"
                className="ml-btn ml-btn-secondary"
                onClick={() => {
                  setDone(false);
                  setEmail("");
                  setCategory("");
                  setDescription("");
                  setAgree(false);
                  setMarketing(false);
                  setErrors({});
                }}
              >
                {t("form.success_again")}
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate>
              {/* Honeypot — ẩn với người thật, bot tự động sẽ điền vào */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: 0, width: 1, height: 1, overflow: "hidden" }}>
                <label>
                  Website
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={hp}
                    onChange={(e) => setHp(e.target.value)}
                  />
                </label>
              </div>

              <h1>{t("form.title")}</h1>
              {app && (
                <div className="ml-app-chip">
                  <img src={app.icon} alt="" />
                  <span>{t("form.app_label")}: {app.short}</span>
                </div>
              )}

              {formError && <div className="ml-form-error">{formError}</div>}

              <div className="ml-field">
                <label htmlFor="ml-email">{t("form.email_label")}</label>
                <input
                  id="ml-email"
                  type="email"
                  className="ml-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("form.email_ph")}
                  autoComplete="email"
                />
                {errors.email && <div className="ml-error">{errors.email}</div>}
              </div>

              <div className="ml-field">
                <label htmlFor="ml-cat">{t("form.category_label")}</label>
                <select
                  id="ml-cat"
                  className="ml-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="" disabled>{t("form.category_ph")}</option>
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{t(c.labelKey)}</option>
                  ))}
                </select>
                {errors.category && <div className="ml-error">{errors.category}</div>}
              </div>

              <div className="ml-field">
                <label htmlFor="ml-desc">{t("form.desc_label")}</label>
                <textarea
                  id="ml-desc"
                  className="ml-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t("form.desc_ph")}
                  maxLength={4000}
                />
                {errors.description && <div className="ml-error">{errors.description}</div>}
              </div>

              <label className="ml-check">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                <span>
                  {t("form.agree_pre")}{" "}
                  <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">{t("nav.privacy")}</a>{" "}
                  {t("form.agree_mid")}{" "}
                  <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">{t("nav.terms")}</a>
                  {t("form.agree_post")}
                </span>
              </label>
              {errors.agree && <div className="ml-error" style={{ marginTop: -8, marginBottom: 12 }}>{errors.agree}</div>}

              <label className="ml-check">
                <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
                <span>{t("form.marketing")}</span>
              </label>

              <button type="submit" className="ml-btn ml-btn-primary ml-btn-block ml-form-submit" disabled={submitting}>
                {submitting ? t("form.submitting") : t("form.submit")}
              </button>

              <div className="ml-form-back">
                <Link to="/">{t("form.back_home")}</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </SiteShell>
  );
}
